import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import CheckoutView from '@/views/CheckoutView.vue'
import { usePermitFormStore } from '@/storage/permitForm'
import { storeToRefs } from 'pinia'
import {
  SupplementalParent,
  useSupplementalFormStore,
} from '@/storage/supplementalForms'
import { useUserPositionStore } from '@/storage/userPosition'
import { LocationRequirementStatus } from '@/types/locationRequirementStatuses'

const guard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const { hasSubmittedStartPermit, hasEndPermit } =
    storeToRefs(usePermitFormStore())
  const shouldShowEndPermit =
    hasSubmittedStartPermit.value && hasEndPermit.value
  if (shouldShowEndPermit) {
    next({ name: 'endPermit' })
    return
  }

  const { forms, routeFrom } = storeToRefs(useSupplementalFormStore())
  const { setCurrentSupplementalForm, setRouteTo, setRouteFrom } =
    useSupplementalFormStore()
  const fromRoute = from.name === 'supplementalForm' ? routeFrom.value : from

  const hasSupplementalForm = Object.values(forms.value).some(
    (parent) => parent.forms.length > 0,
  )

  if (hasSupplementalForm) {
    const firstParentWithForms = Object.keys(forms.value).find(
      (parent) => forms.value[parent].forms.length > 0,
    ) as SupplementalParent

    const supplemental = forms.value[firstParentWithForms].forms[0]

    setCurrentSupplementalForm(firstParentWithForms, supplemental)
    setRouteFrom({
      name: fromRoute?.name as string,
      params: fromRoute?.params,
    } as RouteLocationNormalized)
    setRouteTo({
      name: to.name as string,
      params: to.params,
    } as RouteLocationNormalized)

    next({
      name: 'supplementalForm',
    })
    return
  }

  const { userHasLocationAndIsNotOnSite } = storeToRefs(useUserPositionStore())
  if (userHasLocationAndIsNotOnSite.value) {
    next({ name: 'notOnSite' })
    return
  }

  const { checkLocationRequirements } = useUserPositionStore()
  const passedLocationRequirements = await checkLocationRequirements()
  if (
    from.name !== 'notOnSite' &&
    passedLocationRequirements !== LocationRequirementStatus.Passed
  ) {
    const query =
      passedLocationRequirements === LocationRequirementStatus.PositionError
        ? { positionError: 1 }
        : undefined

    next({ name: 'notOnSite', query: query })
    return
  }

  next()
}

export default {
  beforeEnter: guard,
  component: CheckoutView,
  meta: {
    loadingMessage: 'Loading...',
    requiresLocation: true,
    returnTo: '',
    showMenu: true,
  },
  name: 'checkout',
  path: '/checkout',
}
